/* eslint-disable complexity */
import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Badge,
  Box,
  Button,
  Center,
  Flex,
  Spacer,
  Spinner,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Tooltip,
} from '@chakra-ui/react';
import { memo, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { resolveProperties } from '../../domain/property-operations';
import { Property } from '../../domain/property-types';
import { ProjectSettingsType } from '../../gql/graphql';
import { useDebouncedLoading } from '../../hooks/debounced-loading';
import { useLocalStorage } from '../../hooks/local-storage';
import { usePropertyContext } from '../../hooks/property-resolving';
import { useUserTenant } from '../../services/auth-info';
import { useSelection } from '../../services/viewer/selection';
import { convertToElementIdentifierDbidType } from '../../utils/dbid-utils';
import PlaceholderText from '../text/PlaceholderText';
import { ElementCategory } from '../company/ElementCategories';
import { useProjectHistory } from '../project/project-history/hooks/useProjectHistory';
import { useHistoricalProperties } from '../project/project-history/hooks/useHistoricalProperties';
import BIMPropertiesTab from './BIMPropertiesTab';
import ElementAttributesContent from './ElementAttributesContent';
import { DbIdsPerModel, useViewer, type ModelsMap } from './ForgeViewer';
import { Panel, PanelContent, PanelHeader } from './Panel';
import PropertiesTab from './PropertiesTab';
import { Point } from 'src/domain/geometry/geometric-types';

enum TabIndex {
  PROPERTIES = 0,
  BIM = 1,
}

// To avoid the user accidentally resolving properties for a large number of elements,
const maxSelectedDbIdsBeforeConfirmationRequired = 1;

export default memo(function PropertiesPanel({
  projectId,
  settings,
}: {
  projectId: string;
  settings: ProjectSettingsType | undefined;
}): React.ReactElement {
  const { selectedDbIds } = useSelection();
  const { loadedModels: viewerLoadedModels } = useViewer();
  const { isFreeContractor, isStandardContractor } = useUserTenant();

  const [selectedTab, setSelectedTab] = useLocalStorage<number>(
    'properties-panel-tab-index',
    TabIndex.PROPERTIES
  );

  const [resolveForDbIds, setResolveForDbIds] = useState<DbIdsPerModel>({});

  const numberOfSelectedDbIds = useMemo(
    () => Object.values(selectedDbIds).flat().length,
    [selectedDbIds]
  );
  const hasConfirmedResolve = useMemo(
    () => selectedDbIds === resolveForDbIds,
    [selectedDbIds, resolveForDbIds]
  );

  // Only auto-confirm if we haven't already confirmed and the selection is within limits
  useEffect(() => {
    if (
      numberOfSelectedDbIds <= maxSelectedDbIdsBeforeConfirmationRequired &&
      !hasConfirmedResolve
    ) {
      setResolveForDbIds(selectedDbIds);
    }
  }, [numberOfSelectedDbIds, hasConfirmedResolve, selectedDbIds]);

  const { t } = useTranslation(['common', 'project']);

  const [resolvedProperties, setResolvedProperties] = useState<Property[]>([]);
  const { isHistoryDrawerOpen } = useProjectHistory();

  const [isResolving, setIsResolving] = useDebouncedLoading();

  const propertiesPanelRef = useRef<HTMLDivElement>(null);

  const [propertiesPanelHeight, setPropertiesPanelHeight] = useLocalStorage<
    number | undefined
  >('properties-panel-height', undefined);

  const needsConfirmation = useMemo(
    () =>
      numberOfSelectedDbIds > maxSelectedDbIdsBeforeConfirmationRequired &&
      !hasConfirmedResolve,
    [numberOfSelectedDbIds, hasConfirmedResolve]
  );

  const emptySelection = useMemo(
    () => numberOfSelectedDbIds === 0,
    [numberOfSelectedDbIds]
  );

  const isDisplayingProperties = useMemo(
    () => !isResolving && !needsConfirmation && !emptySelection,
    [isResolving, needsConfirmation, emptySelection]
  );

  const { propertyContext, error: propertyError } =
    usePropertyContext(projectId);

  const cleanedProjectSettings = useMemo(() => {
    return {
      maxTrianglesForComplexAlgorithm:
        settings?.polygonCalculationLimit || 5000,
      globalOffset: [
        settings?.viewerGlobalOffset?.x || 0,
        settings?.viewerGlobalOffset?.y || 0,
        settings?.viewerGlobalOffset?.z || 0,
      ] as Point,
    };
  }, [settings]);

  const {
    isBimSelection,
    propertyDiffMap,
    referenceResolvedProperties,
    newHistoricalPropertyContext,
  } = useHistoricalProperties({
    resolveForDbIds,
    settings,
    propertyContext,
    viewer: {
      loadedModels: viewerLoadedModels,
    },
  });

  useEffect(() => {
    async function loadProperties() {
      const id = Math.random().toString();
      setIsResolving(true, id);
      const dbIdsList = convertToElementIdentifierDbidType(resolveForDbIds);

      // If in history mode and we have a hydrated new historical context, prefer it for BIM props
      // to ensure getBulkProperties2 can run against the right loadedModels
      const ctxForProps =
        isHistoryDrawerOpen && newHistoricalPropertyContext
          ? newHistoricalPropertyContext
          : propertyContext;

      // Ensure models for selected URNs are loaded in the viewer (history may introduce new URNs)
      const selectedUrns = Object.keys(resolveForDbIds);
      // Hydrate models for selected URNs from the live viewer when available
      const mergedModels: ModelsMap = {
        ...(ctxForProps.loadedModels ?? ({} as ModelsMap)),
      } as ModelsMap;
      selectedUrns.forEach((urn) => {
        const m = viewerLoadedModels?.[urn];
        if (m) mergedModels[urn] = m;
      });
      const ctxForPropsHydrated = {
        ...ctxForProps,
        loadedModels: mergedModels,
      };

      const properties = await resolveProperties(
        ctxForPropsHydrated,
        cleanedProjectSettings,
        undefined,
        dbIdsList
      );

      setResolvedProperties(properties);
      setIsResolving(false, id);
    }
    loadProperties();
  }, [
    cleanedProjectSettings,
    isHistoryDrawerOpen,
    newHistoricalPropertyContext,
    propertyContext,
    resolveForDbIds,
    setIsResolving,
    settings?.polygonCalculationLimit,
    viewerLoadedModels,
  ]);

  return (
    <Panel
      resizeable
      panelContentRef={propertiesPanelRef}
      setPanelHeight={setPropertiesPanelHeight}
    >
      <Tabs
        variant="panel-tabs"
        isLazy
        onChange={(index) => setSelectedTab(index)}
        defaultIndex={selectedTab}
      >
        <PanelHeader hasNoMarginBottom={isDisplayingProperties}>
          <Flex w="100%">
            <TabList>
              <Tab>{t('properties')}</Tab>
              {!isFreeContractor && !isStandardContractor && <Tab>BIM</Tab>}

              {settings?.allowedElementsCategories &&
              settings.allowedElementsCategories.length > 0 ? (
                <Tab>{t('attributes')}</Tab>
              ) : null}
            </TabList>
            <Spacer />
            <Box>
              {numberOfSelectedDbIds > 0 && (
                <Tooltip
                  label={t('project:properties.number-of-selected-elements')}
                  aria-label="Number of selected elements"
                >
                  <Badge
                    variant="subtle"
                    colorScheme="blue"
                    borderRadius={'full'}
                    fontSize={'sm'}
                  >
                    {numberOfSelectedDbIds}
                  </Badge>
                </Tooltip>
              )}
            </Box>
          </Flex>
        </PanelHeader>
        {propertyError ? (
          <Center>
            <Alert status="error">
              <AlertIcon />
              <AlertTitle>
                {t('project:properties.error.loading.title')}
              </AlertTitle>
              <AlertDescription>
                {t('project:properties.error.loading.description')}
              </AlertDescription>
            </Alert>
          </Center>
        ) : (
          <PanelContent
            ref={propertiesPanelRef}
            pl={isDisplayingProperties ? 0 : undefined}
            height={propertiesPanelHeight}
          >
            <PropertiesPanelContentMemoized
              isResolving={isResolving}
              emptySelection={emptySelection}
              propertiesForSelection={resolvedProperties}
              dbIds={resolveForDbIds}
              projectId={projectId}
              needsConfirmation={needsConfirmation}
              setConfirmResolve={() => setResolveForDbIds(selectedDbIds)}
              settings={settings}
              referenceResolvedProperties={referenceResolvedProperties}
              propertyDiffMap={
                isHistoryDrawerOpen && !isBimSelection ? propertyDiffMap : {}
              }
              referencePropertyContext={undefined}
              newPropertyContextForDiff={
                isHistoryDrawerOpen
                  ? newHistoricalPropertyContext ?? undefined
                  : undefined
              }
              isFreeContractor={isFreeContractor}
              isStandardContractor={isStandardContractor}
            />
          </PanelContent>
        )}
      </Tabs>
    </Panel>
  );
});

function PropertiesPanelContent({
  isResolving,
  emptySelection,
  propertiesForSelection,
  dbIds,
  projectId,
  needsConfirmation,
  setConfirmResolve,
  settings,
  referenceResolvedProperties,
  propertyDiffMap,
  referencePropertyContext,
  newPropertyContextForDiff,
  isFreeContractor,
  isStandardContractor,
}: {
  isResolving: boolean;
  needsConfirmation: boolean;
  emptySelection: boolean;
  propertiesForSelection: Property[];
  dbIds: DbIdsPerModel;
  projectId: string;
  setConfirmResolve: () => void;
  settings: ProjectSettingsType | undefined;
  referenceResolvedProperties: Property[] | null;
  propertyDiffMap: Record<string, { old?: unknown; new?: unknown }>;
  referencePropertyContext?: any;
  newPropertyContextForDiff?: any;
  isFreeContractor: boolean;
  isStandardContractor: boolean;
}) {
  const { t } = useTranslation(['common', 'project']);
  if (emptySelection) {
    return (
      <PlaceholderText>
        {t('project:properties.no-elements-selected')}
      </PlaceholderText>
    );
  } else if (isResolving) {
    return (
      <Center>
        <Spinner color="brand.400" margin={'auto'} />
      </Center>
    );
  } else if (needsConfirmation) {
    return (
      <Button
        onClick={setConfirmResolve}
        isLoading={isResolving}
        loadingText="Resolving properties"
        variant="ghost"
        size="sm"
        colorScheme="gray"
        p={0}
        w={'100%'}
        justifyContent={'flex-start'}
        height={'auto'}
      >
        {t('project:properties.multiple-elements-selected')}
      </Button>
    );
  } else {
    return (
      <TabPanels>
        <TabPanel>
          <PropertiesTab
            properties={propertiesForSelection}
            selectedDbIds={dbIds}
            projectId={projectId}
            projectSettings={settings}
            referenceResolvedProperties={referenceResolvedProperties}
            propertyDiffMap={propertyDiffMap}
            referencePropertyContext={referencePropertyContext}
            newPropertyContextForDiff={newPropertyContextForDiff}
            isFreeContractor={isFreeContractor}
            isStandardContractor={isStandardContractor}
          />
        </TabPanel>
        {!isFreeContractor && !isStandardContractor && (
          <TabPanel>
            <BIMPropertiesTab
              properties={referenceResolvedProperties ?? propertiesForSelection}
              projectId={projectId}
              projectSettings={settings}
            />
          </TabPanel>
        )}
        <TabPanel>
          {settings?.allowedElementsCategories && (
            <ElementAttributesContent
              resolvedProperties={propertiesForSelection}
              allowedCategories={
                settings.allowedElementsCategories as ElementCategory[]
              }
              projectId={projectId}
              selectedDbIds={dbIds}
            />
          )}
        </TabPanel>
      </TabPanels>
    );
  }
}

const PropertiesPanelContentMemoized = memo(PropertiesPanelContent);
